import { computed, ref } from 'vue'
import type { PaginationArgs } from './types/types'
import useQueryParams from './useQueryParams'

const DEFAULTS = {
  pagesLoadedAtATime: 3,
  preloadPagesAt: 2,
}

export default function usePagination(
  startPage: number = 1,
  pageSize: number = 10,
  fetchData: (pagination: PaginationArgs) => Promise<void>,
  options?: {
    key?: string
    pagesLoadedAtATime?: number
    preloadPagesAt?: number
    knownSize?: number
    prefetchQuery?: (pagination: PaginationArgs) => Promise<void>
    pagesLoaded?: {
      start: number
      end: number
    }
  }
) {
  const localPagesLoadedAtATime = options?.pagesLoadedAtATime || DEFAULTS.pagesLoadedAtATime
  const preloadLocalPagesAt = options?.preloadPagesAt || DEFAULTS.preloadPagesAt

  const { state, updateParams } = useQueryParams<PaginationArgs>(
    {
      page: startPage,
      size: pageSize,
    },
    {
      page: 'p',
      size: 's',
    },
    {
      key: options?.key,
    }
  )

  const currentLocalPage = ref<PaginationArgs>(state.value)
  const queryPagesLoaded = ref(new Set())

  function setItemsLoaded(
    size: number,
    queryPagination: PaginationArgs = { page: 1, size: currentLocalPage.value.size }
  ) {
    queryPagesLoaded.value.add(queryPagination.page)

    if (size < queryPagination.size)
      setTotalSize(size + (queryPagination.page - 1) * queryPagination.size)
  }

  const currentlyKnownMinSize = computed(() => {
    return totalSize.value !== -1
      ? totalSize.value
      : Math.max(...(Array.from(queryPagesLoaded.value) as number[])) *
          currentLocalPage.value.size *
          localPagesLoadedAtATime
  })

  const totalSize = ref(options?.knownSize || -1)

  function setTotalSize(size: number) {
    totalSize.value = size
  }

  function concatenateArrays<T>(oldArray: T[], newArray: T[], pagination: PaginationArgs) {
    const result = [...oldArray]
    const offset = (pagination.page - 1) * pagination.size
    newArray.forEach((item, i) => {
      result[offset + i] = item
    })
    return result
  }

  function visibleItems<T>(arr?: T[]): T[] {
    return (
      arr?.slice(
        (currentLocalPage.value.page - 1) * currentLocalPage.value.size,
        (currentLocalPage.value.page - 1) * currentLocalPage.value.size +
          currentLocalPage.value.size
      ) ?? []
    )
  }

  const isCurrentPageLoaded = computed(() =>
    queryPagesLoaded.value.has(translateFromLocalPageToQueryPage(currentLocalPage.value.page))
  )

  async function loadPages(page: number, shouldPrefetch: boolean = false) {
    const queryPage = translateFromLocalPageToQueryPage(page)
    if (queryPagesLoaded.value.has(queryPage)) return

    const pagination = {
      page: queryPage,
      size: currentLocalPage.value.size * localPagesLoadedAtATime,
    }
    if (shouldPrefetch && options?.prefetchQuery) await options.prefetchQuery(pagination)
    else await fetchData(pagination)
  }

  async function updatePagination(localPage: number, size?: number) {
    const filledPageSize = size ?? currentLocalPage.value.size
    currentLocalPage.value = { page: localPage, size: filledPageSize }
    updateParams({ page: localPage, size: filledPageSize })

    await loadPages(localPage)
    updateIfCloseToEdge(localPage)
  }

  async function updateIfCloseToEdge(localPage: number) {
    const relativePosition = ((localPage - 1) % localPagesLoadedAtATime) + 1
    const previousPage = localPage - localPagesLoadedAtATime
    if (relativePosition < preloadLocalPagesAt && previousPage > 0)
      await loadPages(previousPage, true)

    const nextPage = localPage + localPagesLoadedAtATime
    if (relativePosition > localPagesLoadedAtATime - preloadLocalPagesAt)
      await loadPages(nextPage, true)
  }

  function translateFromQueryPageToLocalPage(queryPage: PaginationArgs) {
    return ((queryPage.page - 1) * queryPage.size) / currentLocalPage.value.size + 1
  }

  function translateFromLocalPageToQueryPage(localPage: number) {
    return (
      Math.floor(
        ((localPage - 1) * currentLocalPage.value.size) /
          (localPagesLoadedAtATime * currentLocalPage.value.size)
      ) + 1
    )
  }

  function incrementPage() {
    updatePagination(currentLocalPage.value.page + 1, currentLocalPage.value.size)
  }

  function decrementPage() {
    updatePagination(currentLocalPage.value.page - 1, currentLocalPage.value.size)
  }

  function reset() {
    updateParams({ page: 1, size: currentLocalPage.value.size })

    currentLocalPage.value = { page: 1, size: currentLocalPage.value.size }
    totalSize.value = -1
    queryPagesLoaded.value = new Set()

    loadPages(1)
  }

  return {
    currentPage: currentLocalPage,
    pagesLoadedAtATime: localPagesLoadedAtATime,
    totalSize,
    currentlyKnownMinSize,
    isCurrentPageLoaded,
    initialPage: state.value.page,
    initialPageSize: state.value.size,
    concatenateArrays,
    translateFromQueryPageToLocalPage,
    translateFromLocalPageToQueryPage,
    setItemsLoaded,
    reset,
    updateIfCloseToEdge,
    visibleItems,
    updatePagination,
    incrementPage,
    decrementPage,
    setTotalSize,
  }
}
